/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useMemo } from "react";
import { BEM, SYSTEM_COLOR } from "@adapptio/lib-components";
import { SCHEMA_COMMENT_STATUS, TSchemaCommentModel } from "@adapptio/lib-blueprint";
import { isFunction } from "@adapptio/lib-shared";
import { IDropdownMenuItem } from "../../../ui/components/DropdownMenu";
import {
	FieldCard,
	FieldCardContent,
	FieldCardHeader,
	FieldCardText
} from "../../../ui/components/Fields/FieldCard";
import { formatDateTimeISO } from "../../../utils/dateTime";
import { ICON_BUTTON_SIZE, IconButton } from "../../../ui/components/IconButton";
import { Markdown } from "../../../ui/components/Markdown";

export interface IInspectorControlCommentsListItemProps {
	model: TSchemaCommentModel;
	index: number;
	onAccept: (index: number) => void;
	onDecline: (index: number) => void;
	onRemove: (index: number) => void;
	onReset: (index: number) => void;
}

export const InspectorControlCommentsListItem: React.FC<IInspectorControlCommentsListItemProps> = ({
	model,
	index,
	onRemove,
	onAccept,
	onDecline,
	onReset
}) => {
	const accepted = model.props.status.value === SCHEMA_COMMENT_STATUS.ACCEPTED;
	const declined = model.props.status.value === SCHEMA_COMMENT_STATUS.DECLINED;
	const resolved = accepted || declined;

	const baseClass = BEM("edt-inspector-control-comments-list-item", null, {
		status: model.props.status.value
	});

	const handleAccept = useCallback(() => {
		if (isFunction(onAccept)) {
			onAccept(index);
		}
	}, [ index, onAccept ]);

	const handleDecline = useCallback(() => {
		if (isFunction(onDecline)) {
			onDecline(index);
		}
	}, [ index, onDecline ]);

	const handleRemove = useCallback(() => {
		if (isFunction(onRemove)) {
			onRemove(index);
		}
	}, [ index, onRemove ]);

	const handleReset = useCallback(() => {
		if (isFunction(onReset)) {
			onReset(index);
		}
	}, [ index, onReset ]);

	const actionsMenuItems = useMemo<IDropdownMenuItem[]>(
		() => [
			{
				id: "unresolve",
				icon: "mdi/cancel",
				label: "Unresolve",
				onClick: handleReset,
				disabled: !resolved
			},
			{
				id: "remove",
				icon: "mdi/delete",
				label: "Remove",
				onClick: handleRemove
			}
		],
		[ handleRemove, resolved, handleReset ]
	);

	return (
		<FieldCard className={baseClass.toClassName()}>
			<FieldCardHeader
				title={model.props.authorName?.value}
				subheader={
					model.props.date?.value ? formatDateTimeISO(model.props.date.value.toISOString()) : ""
				}
				action={
					<>
						<IconButton
							icon="mdi/check"
							tooltip="Accept"
							color={SYSTEM_COLOR.SUCCESS}
							size={ICON_BUTTON_SIZE.XSMALL}
							onClick={handleAccept}
							active={accepted}
							disabled={resolved}
						/>
						<IconButton
							icon="mdi/close"
							tooltip="Decline"
							color={SYSTEM_COLOR.ERROR}
							size={ICON_BUTTON_SIZE.XSMALL}
							onClick={handleDecline}
							active={declined}
							disabled={resolved}
						/>
						<IconButton
							icon="mdi/dots-vertical"
							tooltip="Actions"
							size={ICON_BUTTON_SIZE.XSMALL}
							menuItems={actionsMenuItems}
							hideMenuArrow
						/>
					</>
				}
			/>
			<FieldCardContent>
				<FieldCardText>
					<Markdown>{model.props.message?.value}</Markdown>
				</FieldCardText>
			</FieldCardContent>
		</FieldCard>
	);
};
